<template>
    <div class="relative pb-6">
        <!-- Test Users Section -->
        <template v-if="$page.props.env === 'LOCAL'">
            <div class="mt-5 p-4 max-h-60 border rounded-md border-gray-400 overflow-y-scroll">
                <dl class="divide-y divide-gray-200">
                    <div v-for="user in test_users" :key="user.first + user.last" class="py-4 sm:py-5 sm:grid sm:grid-cols-4 sm:gap-4">
                        <dt class="font-medium">
                            {{ user.first+' '+user.middle+' '+user.last }}
                        </dt>
                        <dd class="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-3">
                            <span class="flex-grow" v-html="user.expected_output"></span>
                            <span class="ml-4 flex-shrink-0">
                                <button type="button" v-on:click="autofill(user)" class="bg-white rounded-md font-medium text-blue-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400">
                                    Autofill
                                </button>
                            </span>
                        </dd>
                    </div>
                </dl>
            </div>
        </template>

        <!-- Multi-Step Form -->
        <div id="multiStepFormDiv" class="bg-gray-50 rounded-lg shadow-md">
            <!-- Progress Header -->
            <div class="p-6">
                <!-- Progress Bar -->
                <div class="bg-white lg:border-b lg:border-t lg:border-gray-200 dark:lg:border-white/15">
                    <nav class="mx-auto max-w-7xl" aria-label="Progress">
                        <ol role="list" class="overflow-hidden rounded-md lg:flex lg:rounded-none lg:border-l lg:border-r lg:border-gray-200 dark:lg:border-white/15">
                            <li v-for="(step, stepIdx) in steps" :key="step.id" class="relative overflow-hidden lg:flex-1">
                            <div :class="[stepIdx === 0 ? 'rounded-t-md border-b-0' : '', stepIdx === steps.length - 1 ? 'rounded-b-md border-t-0' : '', 'overflow-hidden border border-gray-200 lg:border-0 dark:border-white/15']">
                                <span v-if="step.status === 'complete'" class="group">
                                <span class="absolute left-0 top-0 h-full w-1 bg-transparent group-hover:bg-gray-200 lg:bottom-0 lg:top-auto lg:h-1 lg:w-full dark:group-hover:bg-white/20" aria-hidden="true" />
                                <span :class="[stepIdx !== 0 ? 'lg:pl-9' : '', 'flex items-center px-6 py-5 text-sm font-medium']">
                                    <span class="shrink-0">
                                    <span class="flex size-10 items-center justify-center rounded-full bg-green-600 dark:bg-green-500">
                                        <font-awesome-icon :icon="faCheck" class="text-white" aria-hidden="true" />
                                    </span>
                                    </span>
                                    <span class="ml-4 mt-0.5 flex min-w-0 flex-col">
                                    <span class="text-sm font-medium text-gray-900 dark:text-white">{{ step.name }}</span>
                                    <!-- <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ step.description }}</span> -->
                                    </span>
                                </span>
                                </span>
                                <span v-else-if="step.status === 'current'" aria-current="step">
                                <span class="absolute left-0 top-0 h-full w-1 bg-indigo-600 lg:bottom-0 lg:top-auto lg:h-1 lg:w-full dark:bg-indigo-500" aria-hidden="true" />
                                <span :class="[stepIdx !== 0 ? 'lg:pl-9' : '', 'flex items-center px-6 py-5 text-sm font-medium']">
                                    <span class="shrink-0">
                                    <span class="flex size-10 items-center justify-center rounded-full border-2 border-indigo-600 dark:border-indigo-500">
                                        <span class="text-indigo-600 dark:text-indigo-400">{{ step.id }}</span>
                                    </span>
                                    </span>
                                    <span class="ml-4 mt-0.5 flex min-w-0 flex-col">
                                    <span class="text-sm font-medium text-indigo-600 dark:text-indigo-400">{{ step.name }}</span>
                                    <!-- <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ step.description }}</span> -->
                                    </span>
                                </span>
                                </span>
                                <span v-else class="group">
                                <span class="absolute left-0 top-0 h-full w-1 bg-transparent group-hover:bg-gray-200 lg:bottom-0 lg:top-auto lg:h-1 lg:w-full dark:group-hover:bg-white/20" aria-hidden="true" />
                                <span :class="[stepIdx !== 0 ? 'lg:pl-9' : '', 'flex items-center px-6 py-5 text-sm font-medium']">
                                    <span class="shrink-0">
                                    <span class="flex size-10 items-center justify-center rounded-full border-2 border-gray-300 dark:border-white/15">
                                        <span class="text-gray-500 dark:text-gray-400">{{ step.id }}</span>
                                    </span>
                                    </span>
                                    <span class="ml-4 mt-0.5 flex min-w-0 flex-col">
                                    <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ step.name }}</span>
                                    <!-- <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ step.description }}</span> -->
                                    </span>
                                </span>
                                </span>
                                <template v-if="stepIdx !== 0">
                                <!-- Separator -->
                                <div class="absolute inset-0 left-0 top-0 hidden w-3 lg:block" aria-hidden="true">
                                    <svg class="size-full text-gray-300 dark:text-white/15" viewBox="0 0 12 82" fill="none" preserveAspectRatio="none">
                                    <path d="M0.5 0V31L10.5 41L0.5 51V82" stroke="currentcolor" vector-effect="non-scaling-stroke" />
                                    </svg>
                                </div>
                                </template>
                            </div>
                            </li>
                        </ol>
                    </nav>
                </div>
            </div>

            <!-- Form Content -->
            <form class="p-6 pt-0" @submit.prevent="handleSubmit">
                <input id="user_prefs" type="hidden" name="user_prefs"> 
                <input id="user_prefs2" type="hidden" name="user_prefs2">
                
                <!-- Validation Error Alert -->
                <div v-if="showValidationErrors" class="mb-6 rounded-md bg-red-50 p-4" role="alert">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <div class="ml-3">
                            <h3 class="text-sm font-medium text-red-800">Please correct the following errors:</h3>
                            <div class="mt-2 text-sm text-red-700">
                                <ul role="list" class="list-disc space-y-1 pl-5">
                                    <li v-for="error in validationErrorsList" :key="error">{{ error }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Step Components -->
                <personal-info-step 
                    v-if="currentStep === 1"
                    :form="form"
                    :application="application"
                    :applicant_type="applicant_type"
                    :validate-field="validateField"
                    :property="property"
                    :recent_application="recent_application"
                />
                
                <current-address-step
                    v-if="currentStep === 1"
                    :form="form"
                    :application="application"
                    :states="states"
                    :validate-field="validateField"
                />
                <previous-address-step
                    v-if="currentStep === 1"
                    :form="form"
                    :application="application"
                    :states="states"
                    :validate-field="validateField"
                />
                
                 <source-of-income-step
                    v-if="currentStep === 2"
                    :form="form"
                    :application="application"
                    :property="property"
                    :validate-field="validateField"
                />

                <other-information-step
                    v-if="currentStep === 3"
                    :form="form"
                    :application="application"
                    :property="property"
                    :validate-field="validateField"
                    :applicant_type="applicant_type"
                />

                <!-- Step 4: Review & Submit -->
                <review-step 
                    v-if="currentStep === 4"
                    :form="form"
                    :application="application"
                    :states="states"
                    :validate-field="validateField"
                    :applicant_type="applicant_type"
                    :property="property"
                />

                <reference-authorization-step
                    v-if="currentStep === 4"
                    ref="referenceAuthorizationStep"
                    :form="form"
                    :validate-field="validateField"
                />

                <!-- Navigation Buttons -->
                <div class="flex justify-between p-6 mt-4 rounded-md shadow-md border border-gray-200 bg-white">
                    <button 
                        v-if="currentStep > 1"
                        type="button"
                        class="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        @click="previousStep"
                    >
                        Previous
                    </button>
                    <div v-else></div>
                    
                    <div class="flex space-x-3">
                        <button 
                            v-if="currentStep < totalSteps"
                            type="button" 
                            class="primaryButton"
                            @click="nextStep"
                        >
                            Next
                        </button>
                        
                        <loading-button 
                            v-if="currentStep === totalSteps"
                            :form="form" 
                            type="submit" 
                            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                            Save Personal Information
                        </loading-button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import LoadingButton from '@/Shared/LoadingButton.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheck, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useForm } from '@inertiajs/vue3';
import { resolveOwnRentFields } from './mapApplicationToForm';

// Import step components
import PersonalInfoStep from './Steps/PersonalInfoStep.vue';
import CurrentAddressStep from './Steps/CurrentAddressStep.vue';
import PreviousAddressStep from './Steps/PreviousAddressStep.vue';
import SourceOfIncomeStep from './Steps/SourceOfIncomeStep.vue';
import ReviewStep from './Steps/ReviewStep.vue';
import OtherInformationStep from './Steps/OtherInformationStep.vue';
import ReferenceAuthorizationStep from './Steps/ReferenceAuthorizationStep.vue';

export default {
    components: {
        LoadingButton,
        FontAwesomeIcon,
        PersonalInfoStep,
        CurrentAddressStep,
        PreviousAddressStep,
        SourceOfIncomeStep,
        ReviewStep,
        OtherInformationStep,
        ReferenceAuthorizationStep,
    },
    props: {
        property: Object,
        states: Object,
        application: Object,
        test_users: Object,
        applicant_type: Object,
        recent_application: Object,
        experian_attempt: Object,
        step_completion_status: Object,
        step_progress_data: Object,
        completed_steps_map: Object,
    },
    remember: 'form',
    data() {
        return {
            faCheck,
            currentStep: 1,
            totalSteps: 4,
            steps: [
                {'id': '01', 'name': 'My Information', 'description': 'Personal details and address information', 'status': 'current'},
                {'id': '02', 'name': 'Employment & Income', 'description': 'Enter your employment information', 'status': 'upcoming'},
                {'id': '03', 'name': 'Other Information', 'description': 'Vehicles, animals, and additional information', 'status': 'upcoming'},
                {'id': '04', 'name': 'Review & Submit', 'description': 'Review your information and submit', 'status': 'upcoming'},
            ],

            needsPreviousAddress: false,
            disabled: false,
            showAttemptAgainBlock: false,
            faTrash,
            form: useForm({
                user_prefs: null,
                user_prefs2: null,
                user_id: this.application.user_id,
                first_name: '',
                middle_name: '',
                last_name: '',
                phone: '',
                dob: '',
                ssn: '',
                address_1: '',
                address_2: '',
                city: '',
                state: '',
                zip_code: '',
                current_own_rent: '',
                current_monthly_rent: '',
                current_move_in_date: '',
                has_current_landlord: null,
                landlord_name: '',
                landlord_phone: '',
                landlord_email: '',
                has_current_real_estate_agent: null,
                real_estate_agent_name: '',
                real_estate_agent_phone: '',
                real_estate_agent_email: '',
                previous_address_1: '',
                previous_address_2: '',
                previous_city: '',
                previous_state: '',
                previous_zip_code: '',
                previous_monthly_rent: '',
                previous_move_in_date: '',
                has_previous_landlord: null,
                previous_own_rent: '',
                previous_landlord_name: '',
                previous_landlord_phone: '',
                previous_landlord_email: '',
                name_agent_representing: '',
                email_agent_representing: '',
                desired_lease_term: '',
                desired_move_in_date: '',
                willing_rent: '',
                source_of_income_company_name: '',
                source_of_income_job_title: '',
                source_of_income_annual_income: '',
                source_of_income_years: '',
                source_of_income_months: '',
                source_of_income_supervisor_name: '',
                source_of_income_supervisor_phone: '',
                source_of_income_supervisor_email: '',
                user_incomes: [{ key: 0, type: null, amount: null }],
                pets: '',
                additional_occupants: '',
                additional_applicants: [{ name: null, email: null }],
                vehicles: [{ model: null, year: null, color: null }],
                additional_information: '',
                attempt_again: true,
                reference_authorization_agreed: false,
                reference_authorization_signature: '',
            }),
        }
    },
    computed: {
        showValidationErrors() {
            return Object.keys(this.form.errors).length > 0;
        },
        validationErrorsList() {
            return Object.values(this.form.errors);
        }
    },
    watch: {
        'form.current_own_rent'(newValue, oldValue) {
            if (oldValue === '2' && newValue === '1') {
                this.form.landlord_name = '';
                this.form.landlord_phone = '';
                this.form.landlord_email = '';
            }
        },
        'form.previous_own_rent'(newValue, oldValue) {
            if (oldValue === '2' && newValue === '1') {
                this.form.previous_landlord_name = '';
                this.form.previous_landlord_phone = '';
                this.form.previous_landlord_email = '';
            }
        },
        // Watch for changes in the application prop to refresh form data
        // Only populate data when the application ID changes (new application loaded)
        // Not on every property change to avoid overwriting user input
        application: {
            handler(newApplication, oldApplication) {
                if (newApplication && (!oldApplication || newApplication.id !== oldApplication.id)) {
                    this.populateFormData();
                    // Also populate landlord fields when application changes
                    this.$nextTick(() => {
                        this.populateLandlordFields();
                    });
                }
            },
            deep: false // Don't watch deep changes, only top-level changes
        }
    },
    mounted() {
        this.initializePreviousAddressCheck();
        this.populateFormData();
        // Ensure landlord fields are populated on page load
        this.$nextTick(() => {
            this.populateLandlordFields();
        });
    },
    methods: {
        nextStep() {
            if (this.validateCurrentStep()) {
                // Auto-save form data before moving to next step
                this.autoSave().then(() => {
                    // Clear any existing errors and move to next step
                    this.clearValidationErrors();
                    if (this.currentStep < this.totalSteps) {
                        // Mark current step as completed
                        this.updateStepStatus(this.currentStep - 1, 'complete');

                        this.currentStep++;

                        // Mark new step as current
                        this.updateStepStatus(this.currentStep - 1, 'current');

                        // Scroll to top of form section for better UX
                        this.$nextTick(() => {
                            this.scrollToFormTop();
                        });
                    }
                }).catch((errors) => {
                    // Set the validation errors on the form so they display
                    if (errors && typeof errors === 'object') {
                        // Clear existing errors first
                        this.form.clearErrors();

                        // Laravel validation errors are usually in this format
                        Object.keys(errors).forEach(key => {
                            // Handle both array format ['message'] and string format 'message'
                            const errorMessage = Array.isArray(errors[key]) ? errors[key][0] : errors[key];
                            this.form.setError(key, errorMessage);
                        });
                    }
                    // Stay on current step when auto-save fails
                    // Don't move to next step to ensure data integrity
                    // DO NOT call populateFormData() here as it would overwrite user input

                    // Scroll to top when validation errors occur
                    this.$nextTick(() => {
                        this.scrollToFormTop();
                    });
                });
            } else {
                // Validation failed - scroll to top to show errors
                this.$nextTick(() => {
                    this.scrollToFormTop();
                });
            }
        },
        validateCurrentStep() {
            switch (this.currentStep) {
                case 1:
                    // Clear all errors once at the beginning
                    this.form.clearErrors();
                    
                    // Run all validations for step 1 and return true only if all pass
                    const personalInfoValid = this.validatePersonalInfo(false); // false = don't clear errors
                    const currentAddressValid = this.validateCurrentAddress(false); // false = don't clear errors
                    const previousAddressValid = this.validatePreviousAddress(false); // false = don't clear errors
                    return personalInfoValid && currentAddressValid && previousAddressValid;

                case 2:
                    return this.validateSourceOfIncome();
                    
                case 3:
                    return this.validateOtherInformation();
                    
                case 4:
                    return this.validateReviewStep();
                    
                default:
                    return true;
            }
        },
        validatePersonalInfo(clearErrors = true) {
            let hasErrors = false;

            // Clear previous errors only if requested
            if (clearErrors) {
                this.form.clearErrors();
            }

            // Required fields for personal info
            if (!this.form.first_name || this.form.first_name.trim() === '') {
                this.form.setError('first_name', 'First name is required.');
                hasErrors = true;
            }
            
            if (!this.form.last_name || this.form.last_name.trim() === '') {
                this.form.setError('last_name', 'Last name is required.');
                hasErrors = true;
            }

            if (!this.form.phone || this.form.phone.trim() === '') {
                this.form.setError('phone', 'Phone number is required.');
                hasErrors = true;
            }
            
            if(this.form.phone && this.form.phone.length < 10) {
                this.form.setError('phone', 'Phone number must be at exactly 10 digits');
                hasErrors = true;
            }
            
            if (!this.form.dob || this.form.dob.trim() === '') {
                this.form.setError('dob', 'Birthday is required.');
                hasErrors = true;
            }

            if (!this.form.ssn || this.form.ssn.trim() === '' || this.form.ssn.length < 4) {
                this.form.setError('ssn', 'SSN (last 4 digits) is required.');
                hasErrors = true;
            }

            // Additional validation for main applicants
            if (this.applicant_type && this.applicant_type.type === 'main') {
                if (!this.form.desired_move_in_date || this.form.desired_move_in_date.trim() === '') {
                    this.form.setError('desired_move_in_date', 'Desired move-in date is required.');
                    hasErrors = true;
                }
                
                if (!this.form.desired_lease_term || this.form.desired_lease_term.trim() === '') {
                    this.form.setError('desired_lease_term', 'Desired lease term is required.');
                    hasErrors = true;
                }
            }
            
            return !hasErrors;
        },
        validateCurrentAddress(clearErrors = true) {
            let hasErrors = false;

            // Clear previous errors only if requested
            if (clearErrors) {
                this.form.clearErrors('address_1');
                this.form.clearErrors('address_2');
                this.form.clearErrors('city');
                this.form.clearErrors('state');
                this.form.clearErrors('zip_code');
                this.form.clearErrors('current_own_rent');
                this.form.clearErrors('current_monthly_rent');
                this.form.clearErrors('current_move_in_date');
                this.form.clearErrors('has_current_landlord');
                this.form.clearErrors('landlord_name');
                this.form.clearErrors('landlord_phone');
                this.form.clearErrors('landlord_email');
                this.form.clearErrors('has_current_real_estate_agent');
                this.form.clearErrors('real_estate_agent_name');
                this.form.clearErrors('real_estate_agent_phone');
                this.form.clearErrors('real_estate_agent_email');
            }

            // Required fields for current address
            if (!this.form.address_1 || this.form.address_1.trim() === '') {
                this.form.setError('address_1', 'Address 1 is required.');
                hasErrors = true;
            }
            
            if (!this.form.city || this.form.city.trim() === '') {
                this.form.setError('city', 'City is required.');
                hasErrors = true;
            }
            
            if (!this.form.state || this.form.state.trim() === '') {
                this.form.setError('state', 'State is required.');
                hasErrors = true;
            }
            
            if (!this.form.zip_code || this.form.zip_code.trim() === '') {
                this.form.setError('zip_code', 'Zip code is required.');
                hasErrors = true;
            }
            
            if (!this.form.current_own_rent) {
                this.form.setError('current_own_rent', 'Please specify if you own or rent your current address.');
                hasErrors = true;
            }

            if (this.form.has_current_landlord === null || this.form.has_current_landlord === undefined) {
                this.form.setError('has_current_landlord', 'Please specify if you have a current landlord.');
                hasErrors = true;
            }

            if (this.form.has_current_landlord == 1) {
                if (!this.form.landlord_name || this.form.landlord_name.trim() === '') {
                    this.form.setError('landlord_name', 'Landlord name is required.');
                    hasErrors = true;
                }

                if (!this.form.landlord_phone || this.form.landlord_phone.trim() === '') {
                    this.form.setError('landlord_phone', 'Landlord phone is required.');
                    hasErrors = true;
                }

                if (!this.form.landlord_email || this.form.landlord_email.trim() === '') {
                    this.form.setError('landlord_email', 'Landlord email is required.');
                    hasErrors = true;
                }

                if (!this.form.current_monthly_rent || String(this.form.current_monthly_rent).trim() === '') {
                    this.form.setError('current_monthly_rent', 'Monthly rent is required when you list a current landlord.');
                    hasErrors = true;
                }

                if (!this.form.current_move_in_date || this.form.current_move_in_date.trim() === '') {
                    this.form.setError('current_move_in_date', 'Move-in date is required when you list a current landlord.');
                    hasErrors = true;
                }
            }

            if (this.form.has_current_real_estate_agent === null || this.form.has_current_real_estate_agent === undefined) {
                this.form.setError('has_current_real_estate_agent', 'Please specify if you have a current real estate agent.');
                hasErrors = true;
            }

            if (this.form.has_current_real_estate_agent == 1) {
                if (!this.form.real_estate_agent_name || this.form.real_estate_agent_name.trim() === '') {
                    this.form.setError('real_estate_agent_name', 'Real estate agent name is required.');
                    hasErrors = true;
                }

                if (!this.form.real_estate_agent_phone || this.form.real_estate_agent_phone.trim() === '') {
                    this.form.setError('real_estate_agent_phone', 'Real estate agent phone is required.');
                    hasErrors = true;
                }

                if (!this.form.real_estate_agent_email || this.form.real_estate_agent_email.trim() === '') {
                    this.form.setError('real_estate_agent_email', 'Real estate agent email is required.');
                    hasErrors = true;
                }
            }
            
            return !hasErrors;
        },
        validatePreviousAddress(clearErrors = true) {
            let hasErrors = false;

            if (clearErrors) {
                this.form.clearErrors('previous_address_1');
                this.form.clearErrors('previous_city');
                this.form.clearErrors('previous_state');
                this.form.clearErrors('previous_zip_code');
                this.form.clearErrors('previous_own_rent');
                this.form.clearErrors('previous_monthly_rent');
                this.form.clearErrors('previous_move_in_date');
                this.form.clearErrors('has_previous_landlord');
                this.form.clearErrors('previous_landlord_name');
                this.form.clearErrors('previous_landlord_phone');
                this.form.clearErrors('previous_landlord_email');
            }

            if (this.form.has_previous_landlord === null || this.form.has_previous_landlord === undefined) {
                this.form.setError('has_previous_landlord', 'Please specify if you have a previous landlord.');
                hasErrors = true;
            }

            if (this.form.has_previous_landlord != 1) {
                return !hasErrors;
            }

            if (!this.form.previous_landlord_name || this.form.previous_landlord_name.trim() === '') {
                this.form.setError('previous_landlord_name', 'Previous landlord name is required.');
                hasErrors = true;
            }

            if (!this.form.previous_landlord_phone || this.form.previous_landlord_phone.trim() === '') {
                this.form.setError('previous_landlord_phone', 'Previous landlord phone is required.');
                hasErrors = true;
            }

            if (!this.form.previous_landlord_email || this.form.previous_landlord_email.trim() === '') {
                this.form.setError('previous_landlord_email', 'Previous landlord email is required.');
                hasErrors = true;
            }

            if (!this.form.previous_address_1 || this.form.previous_address_1.trim() === '') {
                this.form.setError('previous_address_1', 'Previous address is required when you list a previous landlord.');
                hasErrors = true;
            }

            if (!this.form.previous_city || this.form.previous_city.trim() === '') {
                this.form.setError('previous_city', 'Previous city is required when you list a previous landlord.');
                hasErrors = true;
            }

            if (!this.form.previous_state || this.form.previous_state.trim() === '') {
                this.form.setError('previous_state', 'Previous state is required when you list a previous landlord.');
                hasErrors = true;
            }

            if (!this.form.previous_zip_code || this.form.previous_zip_code.trim() === '') {
                this.form.setError('previous_zip_code', 'Previous zip code is required when you list a previous landlord.');
                hasErrors = true;
            }

            if (!this.form.previous_own_rent) {
                this.form.setError('previous_own_rent', 'Please specify if you owned or rented your previous address.');
                hasErrors = true;
            }

            if (!this.form.previous_monthly_rent || String(this.form.previous_monthly_rent).trim() === '') {
                this.form.setError('previous_monthly_rent', 'Monthly rent is required when you list a previous landlord.');
                hasErrors = true;
            }

            if (!this.form.previous_move_in_date || this.form.previous_move_in_date.trim() === '') {
                this.form.setError('previous_move_in_date', 'Move-in date is required when you list a previous landlord.');
                hasErrors = true;
            }

            return !hasErrors;
        },
        validateSourceOfIncome(clearErrors = true) {
            return true; // return true for now to avoid validation errors as this step is optional            
        },
        validateOtherInformation(clearErrors = true) {
            let hasErrors = false;

            // Clear previous errors only if requested
            if (clearErrors) {
                this.form.clearErrors();
            }

            // Validate vehicles - if any vehicle field is filled, all are required
            if (this.form.vehicles && this.form.vehicles.length > 0) {
                this.form.vehicles.forEach((vehicle, index) => {
                    const hasModel = vehicle.model && vehicle.model.trim() !== '';
                    const hasYear = vehicle.year && vehicle.year.trim() !== '';
                    const hasColor = vehicle.color && vehicle.color.trim() !== '';
                    
                    // If any field is filled, all must be filled
                    if (hasModel || hasYear || hasColor) {
                        if (!hasModel) {
                            this.form.setError(`vehicles.${index}.model`, 'Model is required when other vehicle fields are filled.');
                            hasErrors = true;
                        }
                        if (!hasYear) {
                            this.form.setError(`vehicles.${index}.year`, 'Year is required when other vehicle fields are filled.');
                            hasErrors = true;
                        }
                        if (!hasColor) {
                            this.form.setError(`vehicles.${index}.color`, 'Color is required when other vehicle fields are filled.');
                            hasErrors = true;
                        }
                        
                        // Validate year format and range
                        if (hasYear) {
                            const year = parseInt(vehicle.year);
                            const currentYear = new Date().getFullYear();
                            if (isNaN(year) || year < 1900 || year > currentYear) {
                                this.form.setError(`vehicles.${index}.year`, `Year must be between 1900 and ${currentYear}.`);
                                hasErrors = true;
                            }
                        }
                        
                        // Validate color format (letters, spaces, and dashes only)
                        if (hasColor && !/^[a-zA-Z\s-]+$/.test(vehicle.color)) {
                            this.form.setError(`vehicles.${index}.color`, 'Color can only contain letters, spaces, and dashes.');
                            hasErrors = true;
                        }
                    }
                });
            }

            // Validate additional applicants - if any applicant field is filled, both name and email are required
            if (this.form.additional_applicants && this.form.additional_applicants.length > 0) {
                this.form.additional_applicants.forEach((applicant, index) => {
                    const hasName = applicant.name && applicant.name.trim() !== '';
                    const hasEmail = applicant.email && applicant.email.trim() !== '';
                    
                    // If any field is filled, both must be filled
                    if (hasName || hasEmail) {
                        if (!hasName) {
                            this.form.setError(`additional_applicants.${index}.name`, 'Name is required when email is provided.');
                            hasErrors = true;
                        }
                        if (!hasEmail) {
                            this.form.setError(`additional_applicants.${index}.email`, 'Email is required when name is provided.');
                            hasErrors = true;
                        }
                        
                        // Validate email format
                        if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applicant.email)) {
                            this.form.setError(`additional_applicants.${index}.email`, 'Please enter a valid email address.');
                            hasErrors = true;
                        }
                    }
                });
            }

            // Validate additional information length
            if (this.form.additional_information && this.form.additional_information.length > 2000) {
                this.form.setError('additional_information', 'Additional information cannot exceed 2000 characters.');
                hasErrors = true;
            }
            
            return !hasErrors;
        },
        validateReviewStep(clearErrors = true) {
            let hasErrors = false;

            // Clear previous errors only if requested
            if (clearErrors) {
                this.form.clearErrors();
            }

            // Re-validate all previous steps to ensure everything is complete
            const personalInfoValid = this.validatePersonalInfo(false);
            const currentAddressValid = this.validateCurrentAddress(false);
            const previousAddressValid = this.validatePreviousAddress(false);
            const sourceOfIncomeValid = this.validateSourceOfIncome(false);
            const otherInformationValid = this.validateOtherInformation(false);
            
            if (!personalInfoValid || !currentAddressValid || !previousAddressValid || 
                !sourceOfIncomeValid || !otherInformationValid) {
                hasErrors = true;
            }

            const referenceAuthorizationValid = this.$refs.referenceAuthorizationStep
                ? this.$refs.referenceAuthorizationStep.validateAuthorization(false)
                : true;

            if (!referenceAuthorizationValid) {
                hasErrors = true;
            }
            
            return !hasErrors;
        },
        previousStep() {
            if (this.currentStep > 1) {
                // Auto-save current step data before going back
                this.autoSave().then(() => {
                    // Mark current step as upcoming (since we're going back)
                    this.updateStepStatus(this.currentStep - 1, 'upcoming');

                    this.currentStep--;

                    // Mark new step as current
                    this.updateStepStatus(this.currentStep - 1, 'current');

                    // Refresh form data from application to ensure we have the latest saved data
                    this.populateFormData();

                    // Clear validation errors when moving to previous step
                    this.clearValidationErrors();

                    // Scroll to top of form section for better UX
                    this.$nextTick(() => {
                        this.scrollToFormTop();
                    });
                }).catch((errors) => {
                    // Still allow navigation even if auto-save fails
                    this.updateStepStatus(this.currentStep - 1, 'upcoming');
                    this.currentStep--;
                    this.updateStepStatus(this.currentStep - 1, 'current');
                    this.populateFormData();
                    this.clearValidationErrors();
                    this.$nextTick(() => {
                        this.scrollToFormTop();
                    });
                });
            }
        },
        clearValidationErrors() {
            // Clear all form errors
            this.form.clearErrors();
        },
        scrollToFormTop() {
            // Scroll to the top of the form section smoothly
            const formElement = document.getElementById('multiStepFormDiv');
            if (formElement) {
                formElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            } else {
                // Fallback to scrolling to top of page
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        },
        updateStepStatus(stepIndex, status) {
            if (this.steps[stepIndex]) {
                this.steps[stepIndex].status = status;
            }
        },
        validateField(fieldName) {
            // Clear existing error for this field
            this.form.clearErrors(fieldName);
            
            // Personal Info field validations
            if (fieldName === 'first_name') {
                if (!this.form.first_name || this.form.first_name.trim() === '') {
                    this.form.setError('first_name', 'First name is required.');
                }
            }
            
            if (fieldName === 'last_name') {
                if (!this.form.last_name || this.form.last_name.trim() === '') {
                    this.form.setError('last_name', 'Last name is required.');
                }
            }
            
            if (fieldName === 'phone') {
                if (!this.form.phone || this.form.phone.trim() === '') {
                    this.form.setError('phone', 'Phone number is required.');
                } else if (this.form.phone && this.form.phone.length < 10) {
                    this.form.setError('phone', 'Phone number must be exactly 10 digits');
                }
            }
            
            if (fieldName === 'dob') {
                if (!this.form.dob || this.form.dob.trim() === '') {
                    this.form.setError('dob', 'Birthday is required.');
                }
            }
            
            if (fieldName === 'ssn') {
                if (!this.form.ssn || this.form.ssn.trim() === '' || this.form.ssn.length < 4) {
                    this.form.setError('ssn', 'SSN (last 4 digits) is required.');
                }
            }
            
            if (fieldName === 'desired_move_in_date' && this.applicant_type && this.applicant_type.type === 'main') {
                if (!this.form.desired_move_in_date || this.form.desired_move_in_date.trim() === '') {
                    this.form.setError('desired_move_in_date', 'Desired move-in date is required.');
                }
            }
            
            if (fieldName === 'desired_lease_term' && this.applicant_type && this.applicant_type.type === 'main') {
                if (!this.form.desired_lease_term || this.form.desired_lease_term.trim() === '') {
                    this.form.setError('desired_lease_term', 'Desired lease term is required.');
                }
            }
            
            // Current Address field validations
            if (fieldName === 'address_1') {
                if (!this.form.address_1 || this.form.address_1.trim() === '') {
                    this.form.setError('address_1', 'Address 1 is required.');
                }
            }
            
            if (fieldName === 'city') {
                if (!this.form.city || this.form.city.trim() === '') {
                    this.form.setError('city', 'City is required.');
                }
            }
            
            if (fieldName === 'state') {
                if (!this.form.state || this.form.state.trim() === '') {
                    this.form.setError('state', 'State is required.');
                }
            }
            
            if (fieldName === 'zip_code') {
                if (!this.form.zip_code || this.form.zip_code.trim() === '') {
                    this.form.setError('zip_code', 'Zip code is required.');
                }
            }
            
            if (fieldName === 'current_own_rent') {
                if (!this.form.current_own_rent) {
                    this.form.setError('current_own_rent', 'Please specify if you own or rent your current address.');
                }
            }
            
            // Current landlord validation
            if (fieldName === 'has_current_landlord') {
                if (this.form.has_current_landlord === null || this.form.has_current_landlord === undefined) {
                    this.form.setError('has_current_landlord', 'Please specify if you have a current landlord.');
                }
            }
            
            if (fieldName === 'landlord_name') {
                if (this.form.has_current_landlord == 1 && (!this.form.landlord_name || this.form.landlord_name.trim() === '')) {
                    this.form.setError('landlord_name', 'Landlord name is required.');
                }
            }
            
            if (fieldName === 'landlord_phone') {
                if (this.form.has_current_landlord == 1 && (!this.form.landlord_phone || this.form.landlord_phone.trim() === '')) {
                    this.form.setError('landlord_phone', 'Landlord phone is required.');
                }
            }
            
            if (fieldName === 'landlord_email') {
                if (this.form.has_current_landlord == 1 && (!this.form.landlord_email || this.form.landlord_email.trim() === '')) {
                    this.form.setError('landlord_email', 'Landlord email is required.');
                }
            }
            
            // Current real estate agent validation
            if (fieldName === 'has_current_real_estate_agent') {
                if (this.form.has_current_real_estate_agent === null || this.form.has_current_real_estate_agent === undefined) {
                    this.form.setError('has_current_real_estate_agent', 'Please specify if you have a current real estate agent.');
                }
            }
            
            if (fieldName === 'real_estate_agent_name') {
                if (this.form.has_current_real_estate_agent == 1 && (!this.form.real_estate_agent_name || this.form.real_estate_agent_name.trim() === '')) {
                    this.form.setError('real_estate_agent_name', 'Real estate agent name is required.');
                }
            }
            
            if (fieldName === 'real_estate_agent_phone') {
                if (this.form.has_current_real_estate_agent == 1 && (!this.form.real_estate_agent_phone || this.form.real_estate_agent_phone.trim() === '')) {
                    this.form.setError('real_estate_agent_phone', 'Real estate agent phone is required.');
                }
            }
            
            if (fieldName === 'real_estate_agent_email') {
                if (this.form.has_current_real_estate_agent == 1 && (!this.form.real_estate_agent_email || this.form.real_estate_agent_email.trim() === '')) {
                    this.form.setError('real_estate_agent_email', 'Real estate agent email is required.');
                }
            }
            
            // Previous landlord validation
            if (fieldName === 'has_previous_landlord') {
                if (this.form.has_previous_landlord === null || this.form.has_previous_landlord === undefined) {
                    this.form.setError('has_previous_landlord', 'Please specify if you have a previous landlord.');
                }
            }
            
            if (fieldName === 'previous_landlord_name') {
                if (this.form.has_previous_landlord == 1 && (!this.form.previous_landlord_name || this.form.previous_landlord_name.trim() === '')) {
                    this.form.setError('previous_landlord_name', 'Previous landlord name is required.');
                }
            }
            
            if (fieldName === 'previous_landlord_phone') {
                if (this.form.has_previous_landlord == 1 && (!this.form.previous_landlord_phone || this.form.previous_landlord_phone.trim() === '')) {
                    this.form.setError('previous_landlord_phone', 'Previous landlord phone is required.');
                }
            }
            
            if (fieldName === 'previous_landlord_email') {
                if (this.form.has_previous_landlord == 1 && (!this.form.previous_landlord_email || this.form.previous_landlord_email.trim() === '')) {
                    this.form.setError('previous_landlord_email', 'Previous landlord email is required.');
                }
            }

            if (fieldName === 'previous_address_1') {
                if (this.form.has_previous_landlord == 1 && (!this.form.previous_address_1 || this.form.previous_address_1.trim() === '')) {
                    this.form.setError('previous_address_1', 'Previous address is required when you list a previous landlord.');
                }
            }

            if (fieldName === 'previous_city') {
                if (this.form.has_previous_landlord == 1 && (!this.form.previous_city || this.form.previous_city.trim() === '')) {
                    this.form.setError('previous_city', 'Previous city is required when you list a previous landlord.');
                }
            }

            if (fieldName === 'previous_state') {
                if (this.form.has_previous_landlord == 1 && (!this.form.previous_state || this.form.previous_state.trim() === '')) {
                    this.form.setError('previous_state', 'Previous state is required when you list a previous landlord.');
                }
            }

            if (fieldName === 'previous_zip_code') {
                if (this.form.has_previous_landlord == 1 && (!this.form.previous_zip_code || this.form.previous_zip_code.trim() === '')) {
                    this.form.setError('previous_zip_code', 'Previous zip code is required when you list a previous landlord.');
                }
            }

            if (fieldName === 'previous_own_rent') {
                if (this.form.has_previous_landlord == 1 && !this.form.previous_own_rent) {
                    this.form.setError('previous_own_rent', 'Please specify if you owned or rented your previous address.');
                }
            }

            if (fieldName === 'current_monthly_rent') {
                if (this.form.has_current_landlord == 1 && (!this.form.current_monthly_rent || String(this.form.current_monthly_rent).trim() === '')) {
                    this.form.setError('current_monthly_rent', 'Monthly rent is required when you list a current landlord.');
                }
            }

            if (fieldName === 'current_move_in_date') {
                if (this.form.has_current_landlord == 1 && (!this.form.current_move_in_date || this.form.current_move_in_date.trim() === '')) {
                    this.form.setError('current_move_in_date', 'Move-in date is required when you list a current landlord.');
                }
            }

            if (fieldName === 'previous_monthly_rent') {
                if (this.form.has_previous_landlord == 1 && (!this.form.previous_monthly_rent || String(this.form.previous_monthly_rent).trim() === '')) {
                    this.form.setError('previous_monthly_rent', 'Monthly rent is required when you list a previous landlord.');
                }
            }

            if (fieldName === 'previous_move_in_date') {
                if (this.form.has_previous_landlord == 1 && (!this.form.previous_move_in_date || this.form.previous_move_in_date.trim() === '')) {
                    this.form.setError('previous_move_in_date', 'Move-in date is required when you list a previous landlord.');
                }
            }
            
            // Source of Income field validations
            if (fieldName === 'source_of_income_company_name') {
                if (!this.form.source_of_income_company_name || this.form.source_of_income_company_name.trim() === '') {
                    this.form.setError('source_of_income_company_name', 'Company name is required.');
                }
            }
            
            if (fieldName === 'source_of_income_job_title') {
                if (!this.form.source_of_income_job_title || this.form.source_of_income_job_title.trim() === '') {
                    this.form.setError('source_of_income_job_title', 'Job title is required.');
                }
            }
            
            if (fieldName === 'source_of_income_annual_income') {
                if (!this.form.source_of_income_annual_income || this.form.source_of_income_annual_income.trim() === '') {
                    this.form.setError('source_of_income_annual_income', 'Annual income is required.');
                }
            }
            
            if (fieldName === 'source_of_income_years') {
                if (!this.form.source_of_income_years || this.form.source_of_income_years.trim() === '') {
                    this.form.setError('source_of_income_years', 'Years at current job is required.');
                }
            }
            
            if (fieldName === 'source_of_income_supervisor_name') {
                if (!this.form.source_of_income_supervisor_name || this.form.source_of_income_supervisor_name.trim() === '') {
                    this.form.setError('source_of_income_supervisor_name', 'Supervisor name is required.');
                }
            }
            
            if (fieldName === 'source_of_income_supervisor_phone') {
                if (!this.form.source_of_income_supervisor_phone || this.form.source_of_income_supervisor_phone.trim() === '') {
                    this.form.setError('source_of_income_supervisor_phone', 'Supervisor phone is required.');
                }
            }
            
            if (fieldName === 'willing_rent' && this.applicant_type && this.applicant_type.type === 'main') {
                if (!this.form.willing_rent || this.form.willing_rent.trim() === '') {
                    this.form.setError('willing_rent', 'Please indicate if you are willing to pay the advertised rent.');
                }
            }
            
            // User incomes field validations (Step 2)
            if (fieldName && fieldName.startsWith('user_incomes.')) {
                const parts = fieldName.split('.');
                const index = parseInt(parts[1]);
                const field = parts[2];
                const income = this.form.user_incomes[index];
                
                if (income) {
                    const hasType = income.type && income.type.trim() !== '';
                    const hasAmount = income.amount !== null && income.amount !== '';
                    
                    // If any field is filled, both must be filled
                    if (hasType || hasAmount) {
                        if (field === 'type' && !hasType) {
                            this.form.setError(`user_incomes.${index}.type`, 'Income type is required when amount is provided.');
                        }
                        if (field === 'amount' && !hasAmount) {
                            this.form.setError(`user_incomes.${index}.amount`, 'Income amount is required when type is provided.');
                        }
                        
                        // Validate amount is numeric and within range
                        if (field === 'amount' && hasAmount) {
                            const amount = parseFloat(income.amount);
                            if (isNaN(amount) || amount < 0 || amount > 99999999.99) {
                                this.form.setError(`user_incomes.${index}.amount`, 'Income amount must be between $0.00 and $99,999,999.99.');
                            }
                        }
                    }
                }
            }
            
            // Other Information field validations (Step 3)
            if (fieldName && fieldName.startsWith('vehicles.')) {
                const parts = fieldName.split('.');
                const index = parseInt(parts[1]);
                const field = parts[2];
                const vehicle = this.form.vehicles[index];
                
                if (vehicle) {
                    const hasModel = vehicle.model && vehicle.model.trim() !== '';
                    const hasYear = vehicle.year && vehicle.year.trim() !== '';
                    const hasColor = vehicle.color && vehicle.color.trim() !== '';
                    
                    // If any field is filled, all must be filled
                    if (hasModel || hasYear || hasColor) {
                        if (field === 'model' && !hasModel) {
                            this.form.setError(`vehicles.${index}.model`, 'Model is required when other vehicle fields are filled.');
                        }
                        if (field === 'year' && !hasYear) {
                            this.form.setError(`vehicles.${index}.year`, 'Year is required when other vehicle fields are filled.');
                        }
                        if (field === 'color' && !hasColor) {
                            this.form.setError(`vehicles.${index}.color`, 'Color is required when other vehicle fields are filled.');
                        }
                        
                        // Validate year format and range
                        if (field === 'year' && hasYear) {
                            const year = parseInt(vehicle.year);
                            const currentYear = new Date().getFullYear();
                            if (isNaN(year) || year < 1900 || year > currentYear) {
                                this.form.setError(`vehicles.${index}.year`, `Year must be between 1900 and ${currentYear}.`);
                            }
                        }
                        
                        // Validate color format (letters, spaces, and dashes only)
                        if (field === 'color' && hasColor && !/^[a-zA-Z\s-]+$/.test(vehicle.color)) {
                            this.form.setError(`vehicles.${index}.color`, 'Color can only contain letters, spaces, and dashes.');
                        }
                    }
                }
            }
            
            if (fieldName && fieldName.startsWith('additional_applicants.')) {
                const parts = fieldName.split('.');
                const index = parseInt(parts[1]);
                const field = parts[2];
                const applicant = this.form.additional_applicants[index];
                
                if (applicant) {
                    const hasName = applicant.name && applicant.name.trim() !== '';
                    const hasEmail = applicant.email && applicant.email.trim() !== '';
                    
                    // If any field is filled, both must be filled
                    if (hasName || hasEmail) {
                        if (field === 'name' && !hasName) {
                            this.form.setError(`additional_applicants.${index}.name`, 'Name is required when email is provided.');
                        }
                        if (field === 'email' && !hasEmail) {
                            this.form.setError(`additional_applicants.${index}.email`, 'Email is required when name is provided.');
                        }
                        
                        // Validate email format
                        if (field === 'email' && hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applicant.email)) {
                            this.form.setError(`additional_applicants.${index}.email`, 'Please enter a valid email address.');
                        }
                    }
                }
            }
            
            if (fieldName === 'additional_information') {
                if (this.form.additional_information && this.form.additional_information.length > 2000) {
                    this.form.setError('additional_information', 'Additional information cannot exceed 2000 characters.');
                }
            }
        },
        handleSubmit() {
            // Only submit when on the final step
            if (this.currentStep === this.totalSteps) {
                this.update();
            }
        },
        initializePreviousAddressCheck() {
            // Logic to determine if previous address is needed
            this.needsPreviousAddress = false;
        },
        populateFormData() {
            if (this.application) {
                // Don't overwrite user input if form already has data
                // Only populate if form is mostly empty (first load)
                const hasUserInput = this.form.first_name || this.form.last_name || this.form.phone || 
                                   this.form.address_1 || this.form.city || this.form.state;
                
                if (hasUserInput) {
                    // Still populate landlord fields even if other fields have data
                    this.populateLandlordFields();
                    return; // Don't overwrite existing user input for other fields
                }
                // Personal Information
                this.form.first_name = this.application.first_name || '';
                this.form.middle_name = this.application.middle_name || '';
                this.form.last_name = this.application.last_name || '';
                this.form.phone = this.application.home_phone || this.application.phone || '';
                this.form.dob = this.application.birthday || this.application.dob || '';
                this.form.ssn = this.application.last_4_ssn || this.application.ssn || '';
                
                // Current Address
                this.form.address_1 = this.application.address_1 || '';
                this.form.address_2 = this.application.address_2 || '';
                this.form.city = this.application.city || '';
                this.form.state = this.application.state || '';
                this.form.zip_code = this.application.zip_code || '';
                this.form.current_own_rent = resolveOwnRentFields(this.application).current_own_rent;
                this.form.current_monthly_rent = this.application.current_monthly_rent || '';
                this.form.current_move_in_date = this.application.current_move_in_date || '';
                this.form.has_current_landlord = this.application.has_current_landlord !== null && this.application.has_current_landlord !== undefined ? (this.application.has_current_landlord ? 1 : 0) : null;
                this.form.landlord_name = this.application.landlord_name || '';
                this.form.landlord_phone = this.application.landlord_phone || '';
                this.form.landlord_email = this.application.landlord_email || '';
                this.form.has_current_real_estate_agent = this.application.has_current_real_estate_agent !== null && this.application.has_current_real_estate_agent !== undefined ? (this.application.has_current_real_estate_agent ? 1 : 0) : null;
                this.form.real_estate_agent_name = this.application.real_estate_agent_name || '';
                this.form.real_estate_agent_phone = this.application.real_estate_agent_phone || '';
                this.form.real_estate_agent_email = this.application.real_estate_agent_email || '';
                
                // Previous Address
                this.form.previous_address_1 = this.application.addresses?.previous_address_1 || '';
                this.form.previous_address_2 = this.application.addresses?.previous_address_2 || '';
                this.form.previous_city = this.application.addresses?.previous_city || '';
                this.form.previous_state = this.application.addresses?.previous_state || '';
                this.form.previous_zip_code = this.application.addresses?.previous_zip_code || '';
                this.form.previous_monthly_rent = this.application.addresses?.previous_monthly_rent || '';
                this.form.previous_move_in_date = this.application.addresses?.previous_move_in_date || '';
                this.form.has_previous_landlord = this.application.addresses && this.application.addresses.has_previous_landlord !== null && this.application.addresses.has_previous_landlord !== undefined ? (this.application.addresses.has_previous_landlord ? 1 : 0) : 0;
                this.form.previous_own_rent = resolveOwnRentFields(this.application).previous_own_rent;
                this.form.previous_landlord_name = this.application.addresses?.previous_landlord_name || '';
                this.form.previous_landlord_phone = this.application.addresses?.previous_landlord_phone || '';
                this.form.previous_landlord_email = this.application.addresses?.previous_landlord_email || '';
                
                // Populate landlord fields
                this.populateLandlordFields();
                
                // Application Details
                this.form.desired_move_in_date = this.application.desired_move_in_date || '';
                this.form.desired_lease_term = this.application.desired_lease_term || '';
                this.form.willing_rent = this.application.willing_rent || '';
                this.form.name_agent_representing = this.application.name_agent_representing || '';
                this.form.email_agent_representing = this.application.email_agent_representing || '';
                
                // Employment & Income
                this.form.source_of_income_company_name = this.application.company_name || this.application.source_of_income_company_name || '';
                this.form.source_of_income_job_title = this.application.job_title || this.application.source_of_income_job_title || '';
                this.form.source_of_income_annual_income = this.application.annual_income || this.application.source_of_income_annual_income || '';
                this.form.source_of_income_years = this.application.source_of_income_years || '';
                this.form.source_of_income_months = this.application.source_of_income_months || '';
                this.form.source_of_income_supervisor_name = this.application.supervisor_name || this.application.source_of_income_supervisor_name || '';
                this.form.source_of_income_supervisor_phone = this.application.supervisor_phone || this.application.source_of_income_supervisor_phone || '';
                this.form.source_of_income_supervisor_email = this.application.supervisor_email || this.application.source_of_income_supervisor_email || '';
                
                // Other Information
                this.form.pets = this.application.pets || '';
                this.form.additional_occupants = this.application.additional_occupants || '';
                this.form.additional_information = this.application.additional_information || '';
                this.form.reference_authorization_agreed = !!this.application.reference_authorization_at;
                this.form.reference_authorization_signature = this.application.reference_authorization_signature || '';
                
                // Additional applicants and vehicles arrays
                if (this.application.additional_applicants) {
                    this.form.additional_applicants = this.application.additional_applicants;
                }
                if (this.application.vehicles) {
                    this.form.vehicles = this.application.vehicles;
                }
                if (this.application.user_incomes) {
                    this.form.user_incomes = this.application.user_incomes;
                }
            }
        },
        populateLandlordFields() {
            if (this.application) {
                // Current landlord fields
                if (this.form.has_current_landlord === null) {
                    this.form.has_current_landlord = this.application.has_current_landlord !== null && this.application.has_current_landlord !== undefined ? (this.application.has_current_landlord ? 1 : 0) : null;
                }
                if (this.form.landlord_name === '') {
                    this.form.landlord_name = this.application.landlord_name || '';
                }
                if (this.form.landlord_phone === '') {
                    this.form.landlord_phone = this.application.landlord_phone || '';
                }
                if (this.form.landlord_email === '') {
                    this.form.landlord_email = this.application.landlord_email || '';
                }
                
                // Current real estate agent fields
                if (this.form.has_current_real_estate_agent === null) {
                    this.form.has_current_real_estate_agent = this.application.has_current_real_estate_agent !== null && this.application.has_current_real_estate_agent !== undefined ? (this.application.has_current_real_estate_agent ? 1 : 0) : null;
                }
                if (this.form.real_estate_agent_name === '') {
                    this.form.real_estate_agent_name = this.application.real_estate_agent_name || '';
                }
                if (this.form.real_estate_agent_phone === '') {
                    this.form.real_estate_agent_phone = this.application.real_estate_agent_phone || '';
                }
                if (this.form.real_estate_agent_email === '') {
                    this.form.real_estate_agent_email = this.application.real_estate_agent_email || '';
                }
                
                // Previous landlord fields
                if (this.form.has_previous_landlord === null) {
                    this.form.has_previous_landlord = this.application.addresses && this.application.addresses.has_previous_landlord !== null && this.application.addresses.has_previous_landlord !== undefined ? (this.application.addresses.has_previous_landlord ? 1 : 0) : null;
                }
                if (this.form.previous_landlord_name === '') {
                    this.form.previous_landlord_name = this.application.addresses?.previous_landlord_name || '';
                }
                if (this.form.previous_landlord_phone === '') {
                    this.form.previous_landlord_phone = this.application.addresses?.previous_landlord_phone || '';
                }
                if (this.form.previous_landlord_email === '') {
                    this.form.previous_landlord_email = this.application.addresses?.previous_landlord_email || '';
                }
            }
        },
        isDisabled() {
            this.disabled = !this.disabled;
            this.form.middle_name = this.disabled ? '' : this.form.middle_name;
        },
        getStepFields(step) {
            const stepFieldMappings = {
                1: [
                    // Personal Info fields
                    'first_name', 'middle_name', 'last_name', 'phone', 'dob', 'ssn',
                    'desired_move_in_date', 'desired_lease_term',
                    // Current Address fields
                    'address_1', 'address_2', 'city', 'state', 'zip_code', 'current_own_rent',
                    'current_monthly_rent', 'current_move_in_date',
                    'has_current_landlord', 'landlord_name', 'landlord_phone', 'landlord_email',
                    'has_current_real_estate_agent', 'real_estate_agent_name', 'real_estate_agent_phone', 'real_estate_agent_email',
                    // Previous Address fields
                    'previous_address_1', 'previous_address_2', 'previous_city', 'previous_state', 
                    'previous_zip_code', 'previous_monthly_rent', 'previous_move_in_date', 'has_previous_landlord', 'previous_own_rent', 'previous_landlord_name', 
                    'previous_landlord_phone', 'previous_landlord_email'
                ],
                2: [
                    // Employment & Income fields
                    'willing_rent', 'source_of_income_company_name', 'source_of_income_job_title',
                    'source_of_income_annual_income', 'source_of_income_years', 'source_of_income_months',
                    'source_of_income_supervisor_name', 'source_of_income_supervisor_phone', 
                    'source_of_income_supervisor_email', 'user_incomes'
                ],
                3: [
                    // Other Information fields
                    'pets', 'additional_occupants', 'additional_applicants', 'vehicles', 
                    'additional_information'
                ],
                4: [
                    // Review step - no new fields, just validation
                ]
            };
            
            return stepFieldMappings[step] || [];
        },
        getFirstStepWithErrors() {
            const errorKeys = Object.keys(this.form.errors);

            if (errorKeys.length === 0) {
                return this.currentStep;
            }

            for (let step = 1; step <= this.totalSteps; step++) {
                const fields = step === 4
                    ? ['reference_authorization_agreed', 'reference_authorization_signature']
                    : this.getStepFields(step);

                const hasStepError = errorKeys.some((key) => {
                    if (fields.includes(key)) {
                        return true;
                    }

                    if (step === 3) {
                        return key.startsWith('additional_applicants') || key.startsWith('vehicles');
                    }

                    return false;
                });

                if (hasStepError) {
                    return step;
                }
            }

            return 1;
        },
        setCurrentStepFromValidation(errorStep) {
            this.currentStep = errorStep;

            for (let i = 0; i < this.totalSteps; i++) {
                if (i < errorStep - 1) {
                    this.updateStepStatus(i, 'complete');
                } else if (i === errorStep - 1) {
                    this.updateStepStatus(i, 'current');
                } else {
                    this.updateStepStatus(i, 'upcoming');
                }
            }
        },
        autoSave() {
            // Return a promise for async handling
            return new Promise((resolve, reject) => {
                // Save ALL form data, not just current step fields
                // This ensures data persists when navigating between steps
                const formData = {};
                
                // Include all form fields to preserve data across steps
                Object.keys(this.form.data()).forEach(field => {
                    if (this.form.hasOwnProperty(field)) {
                        formData[field] = this.form[field];
                    }
                });
                
                // Always include user preferences
                formData.user_prefs = typeof adx !== 'undefined' ? adx.validate('user_prefs') : null;
                formData.user_prefs2 = typeof adx !== 'undefined' ? adx.validate('user_prefs2') : null;
                
                // Use Inertia to auto-save without navigating
                this.$inertia.put(this.route('application.auto-save-personal-information', this.application.id), formData, {
                    preserveState: true,
                    preserveScroll: true,
                    only: [], // Don't reload any props
                    onSuccess: () => {
                        resolve();
                    },
                    onError: (errors) => {
                        reject(errors);
                    }
                });
            });
        },
        update() {
            if (!this.validateReviewStep()) {
                this.setCurrentStepFromValidation(this.getFirstStepWithErrors());
                this.$nextTick(() => {
                    this.scrollToFormTop();
                });
                return;
            }

            this.form.user_prefs = typeof adx !== 'undefined' ? adx.validate('user_prefs') : null;
            this.form.user_prefs2 = typeof adx !== 'undefined' ? adx.validate('user_prefs2') : null;
            this.form.post(this.route('application.update-personal-information', this.application.id), {
                onFinish: visit => {
                    this.checkMaximumAttempts();
                },
            });
        },
        autofill(person) {
            this.form.first_name = person.first;
            this.form.middle_name = person.middle;
            this.form.last_name = person.last;
            this.form.address_1 = person.address;
            this.form.city = person.city;
            this.form.state = person.state;
        },
        checkMaximumAttempts() {
            if (this.application.experian_attempts_count >= 5) {
                this.showAttemptAgainBlock = false;
                this.form.attempt_again = false;
            }
        },
    }
}
</script>